import { useIsAuthenticated } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { getGatewayListByUserId } from "api/common/OnAppLoad/getDataOnLoad";
import { GatewayData } from "pages/Breakers/typings";
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectGateway } from "redux/slices/gatewaySlice";
import { RootState } from "redux/store/store";
import { signalRService } from "services/signalRService";
import { getUserId } from "utils/Common";

const useProtectedRoute = () => {
  const [isLoading, setIsLoading] = useState(true);

  const userId = getUserId(sessionStorage.getItem("userId"));
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useDispatch();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  //  REDUX-PERSIST RESTORED VALUE
  const selectedGateway = useSelector(
    (state: RootState) => state.gateway.selectedGateway,
  );

  //  Gateway list query
  const {
    data: GatewayListData,
    refetch: GatewayListRefetch,
    isFetched,
  } = useQuery<GatewayData[], Error>({
    queryKey: ["gatewayList"],
    queryFn: () => getGatewayListByUserId({ userId }),
    enabled: isAuthenticated,
  });

  //  Handle gateway update feed
  const handleGatewayUpdateFeed = () => {
    GatewayListRefetch();
  };

  //  Register SignalR and setup listeners
  const registerSignalRAndSetupListeners = async (connectionId: string) => {
    sessionStorage.setItem("signalRConnectionId", String(connectionId));

    await signalRService.invoke(
      "Register",
      userId,
      `${userId}`,
      connectionId,
      1,
    );
    signalRService.on("GatewayUpdateFeedReceived", handleGatewayUpdateFeed);
  };

  //  Handle connection setup
  const handleConnectionSetup = async () => {
    try {
      const connectionId =
        await signalRService.invoke<string>("GetConnectionId");

      if (connectionId) {
        await registerSignalRAndSetupListeners(connectionId);
      } else {
        console.error("Connection ID is null, registration failed.");
      }
    } catch (error) {
      console.error("Failed to setup SignalR connection:", error);
    }
  };

  //  Handle successful SignalR connection
  const handleConnectionSuccess = () => {
    setIsLoading(false);
  };

  const initializeSignalR = async () => {
    try {
      await signalRService.startConnection();
      handleConnectionSuccess();

      // Register reconnect handler after connection is established
      signalRService.onReconnected(async () => {
        await handleConnectionSetup();
      });

      // slight delay for stability
      timerRef.current = setTimeout(handleConnectionSetup, 500);
    } catch (error) {
      console.error("Failed to start SignalR connection:", error);
      setIsLoading(false);
    }
  };

  //  Handle reconnect — removed from component body; now registered inside initializeSignalR

  //  Loading control
  useEffect(() => {
    setIsLoading(!isFetched);
  }, [isFetched]);

  //   REDUX-PERSIST SAFE GATEWAY SELECTION LOGIC
  const handleGatewaySelection = (gatewayList: GatewayData[]) => {
    if (selectedGateway) {
      const matchingGateway = gatewayList.find(
        (gw) => gw.id === selectedGateway.id,
      );

      if (matchingGateway) {
        dispatch(selectGateway(matchingGateway));
        return;
      }
    }

    dispatch(selectGateway(gatewayList[0]));
  };

  useEffect(() => {
    if (GatewayListData !== undefined && GatewayListData.length > 0) {
      handleGatewaySelection(GatewayListData);
    }
  }, [GatewayListData]);

  //  Init and cleanup SignalR
  useEffect(() => {
    if (isAuthenticated) initializeSignalR();

    return () => {
      signalRService.invoke(
        "Deregister",
        sessionStorage.getItem("userId"),
        sessionStorage.getItem("signalRConnectionId"),
      );

      signalRService.off("BreakerLiveFeedReceived");
      signalRService.off("NotificationReceived");
      signalRService.off("GatewayUpdateFeedReceived");

      signalRService.stopConnection();
      clearTimeout(timerRef.current as NodeJS.Timeout);
    };
  }, [isAuthenticated]);

  return {
    isAuthenticated,
    GatewayListData,
    isLoading,
    GatewayListRefetch,
  };
};

export default useProtectedRoute;
